import { AlertColor } from '@mui/material'
import {
  createAsyncThunk,
  createSlice,
  isAnyOf,
  PayloadAction,
} from '@reduxjs/toolkit'
import { RootState } from '..'
import { IChatUser } from '../../types/messagesTypes'
import okaoAxios from '../../helpers/axios'
import axios from 'axios'

export interface IReviewSlice {
  message: string | undefined
  severity: AlertColor | undefined
  shouldOpen: boolean
  status: string
  users: IChatUser[]
  selectedUser: IChatUser | undefined
  chatToken: string | undefined
}

const initialState: IReviewSlice = {
  status: 'idle',
  shouldOpen: false,
  message: undefined,
  severity: undefined,
  users: [],
  selectedUser: undefined,
  chatToken: undefined,
}

export const fetchChatToken = createAsyncThunk<string, string>(
  'api/fetchChatToken',
  async (userType, { rejectWithValue }) => {
    try {
      const response = await okaoAxios.get(`/chats/auth/${userType}`)
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue({
          message: error.response?.data?.message || error.message,
          status: error.response?.status,
        })
      }
      return rejectWithValue({ message: 'An unknown error occurred' })
    }
  },
)

export const messageSlice = createSlice({
  name: 'restaurant-message',
  initialState,
  reducers: {
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.shouldOpen = action.payload
    },

    setSelectedUser: (state, action: PayloadAction<IChatUser | undefined>) => {
      state.selectedUser = action.payload
    },
    clearData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChatToken.fulfilled, (state, action) => {
        state.status = 'idle'
        state.chatToken = action.payload
        window.localStorage.setItem(
          'chat-token',
          JSON.stringify(action.payload),
        )
      })
      .addCase(fetchChatToken.rejected, (state, action) => {
        state.chatToken = undefined
        state.shouldOpen = true
        state.severity = 'error'
        state.status = 'error'
        state.message =
          'Oops! Looks like something went wrong on our end. But don’t worry, a quick refresh might just do the trick! Give it a whirl and let’s try this again. If the problem persists, we’ll be here to sort it out!'
      })
      .addMatcher(isAnyOf(fetchChatToken.pending), (state, action) => {
        state.status = 'loading'
      })
  },
})

export const selectIsLoading = (state: RootState): boolean =>
  state?.restaurantMessage?.status === 'loading'
export const selectShouldOpen = (state: RootState): boolean =>
  state?.restaurantMessage?.shouldOpen
export const selectMessage = (state: RootState): string | undefined =>
  state?.restaurantMessage?.message
export const selectSeverity = (state: RootState): AlertColor | undefined =>
  state?.restaurantMessage?.severity

export const selectSelectedUser = (state: RootState): IChatUser | undefined =>
  state?.restaurantMessage?.selectedUser
export const selectChatToken = (state: RootState): string | undefined => {
  return state?.restaurantMessage?.chatToken
}

export const { setOpen, setSelectedUser, clearData } = messageSlice.actions

export default messageSlice.reducer
